import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Message} from '../../../contracts/message.interface';
import {Conversation} from '../../../contracts/conversation.interface';
import {ContactDisplayPipe} from '../../../../shared/pipes/contact-display.pipe';
import {Image} from 'primeng/image';
import {ConversationService} from '../../../services/conversation.service';
import {ConversationInitialsComponent} from '../../../../shared/components/conversation-initials/conversation-initials.component';
import {SettingsService} from '../../../../shared/services/settings_service';

@Component({
  standalone: true,
  selector: 'app-chat-side-panel-media',
  imports: [
    CommonModule,
    FormsModule,
    ContactDisplayPipe,
    Image,
    ConversationInitialsComponent,
  ],
  templateUrl: './chat-sidepanel-media.component.html',
  styleUrls: ['./chat-sidepanel-media.component.scss']
})
export class ChatSidePanelMediaComponent implements OnChanges {
  @Input() conversation!: Conversation;

  media: Message[] = [];
  quotes: Message[] = [];
  selectedMedia?: Message;
  selectedQuote?: Message;
  copiedPlate: string | null = null;
  aiDetailExpanded = false;

  hideLoadMoreMedia = false;
  hideLoadMoreQuotes = false;
  private mediaLoading = false;
  private quotesLoading = false;

  private mediaSkip = 0;
  private quotesSkip = 0;
  private readonly pageSize = 16;

  constructor(private conversationService: ConversationService, private settingsService: SettingsService) {}

  get audioTranscriptionEnabled(): boolean {
    return this.settingsService.getTenantSettings()?.features?.audioTranscription?.enabled ?? false;
  }

  get plateDetectionEnabled(): boolean {
    return this.settingsService.getTenantSettings()?.features?.plateDetection?.enabled ?? false;
  }

  get primaryPhone(): string | null {
    const nums = this.conversation.contact?.phoneNumbers;
    if (!nums?.length) return this.conversation.fromNumber || null;
    const primary = nums.find(p => p.isPrimary) ?? nums[0];
    return primary.phoneNumber;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['conversation'] && changes['conversation'].currentValue) {
      const prevConv: Conversation | undefined = changes['conversation'].previousValue;
      const newConv: Conversation = changes['conversation'].currentValue;

      if (!prevConv || prevConv.id !== newConv.id) {
        this.selectedMedia = undefined;
        this.selectedQuote = undefined;
        this.mediaSkip = 0;
        this.quotesSkip = 0;
        this.hideLoadMoreMedia = false;
        this.hideLoadMoreQuotes = false;
        this.mediaLoading = false;
        this.quotesLoading = false;
        this.loadData();
      }
    }
  }

  private loadData() {
    this.mediaSkip = 0;
    this.quotesSkip = 0;

    this.conversationService.getMedia(this.conversation.id, this.mediaSkip, this.pageSize)
      .subscribe(media => {
        this.media = media;
        this.mediaSkip += media.length;
        this.hideLoadMoreMedia = media.length < this.pageSize;
      });

    this.conversationService.getQuotes(this.conversation.id, this.quotesSkip, this.pageSize)
      .subscribe(quotes => {
        this.quotes = quotes;
        this.quotesSkip += quotes.length;
        this.hideLoadMoreQuotes = quotes.length < this.pageSize;
      });
  }

  selectMedia(item: Message) {
    this.selectedMedia = this.selectedMedia === item ? undefined : item;
    this.aiDetailExpanded = false;
  }

  selectQuote(item: Message) {
    this.selectedQuote = this.selectedQuote === item ? undefined : item;
  }

  openDocument(item: Message) {
    if (item?.mediaUrl) window.open(item.mediaUrl, '_blank');
  }

  copyPlate(plate: string): void {
    navigator.clipboard.writeText(plate).then(() => {
      this.copiedPlate = plate;
      setTimeout(() => (this.copiedPlate = null), 2000);
    });
  }

  mimeCategory(msg: Message): string {
    return msg.mimeType?.split('/')[0] ?? 'application';
  }

  hasAiContent(msg: Message): boolean {
    const ai = msg.aiInsights;
    if (!ai) return false;
    return (this.audioTranscriptionEnabled && !!ai.audioTranscription) ||
           (this.plateDetectionEnabled && !!ai.plateDetection);
  }

  onMediaGridScroll(event: Event): void {
    if (this.hideLoadMoreMedia || this.mediaLoading) return;
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      this.loadMore('media');
    }
  }

  onQuotesGridScroll(event: Event): void {
    if (this.hideLoadMoreQuotes || this.quotesLoading) return;
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      this.loadMore('quotes');
    }
  }

  loadMore(type: 'media' | 'quotes') {
    if (type === 'media') {
      if (this.mediaLoading) return;
      this.mediaLoading = true;
      this.conversationService.getMedia(this.conversation.id, this.mediaSkip, this.pageSize)
        .subscribe(newMedia => {
          this.media = [...this.media, ...newMedia];
          this.mediaSkip += newMedia.length;
          this.hideLoadMoreMedia = newMedia.length < this.pageSize;
          this.mediaLoading = false;
        });
    } else {
      if (this.quotesLoading) return;
      this.quotesLoading = true;
      this.conversationService.getQuotes(this.conversation.id, this.quotesSkip, this.pageSize)
        .subscribe(newQuotes => {
          this.quotes = [...this.quotes, ...newQuotes];
          this.quotesSkip += newQuotes.length;
          this.hideLoadMoreQuotes = newQuotes.length < this.pageSize;
          this.quotesLoading = false;
        });
    }
  }
}
